import { FC } from 'react'

import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessTemplateBasicInfoFragment,
  UpdatePerformanceProcessTemplateBasicInfoDocument,
  UpdatePerformanceProcessTemplateBasicInfoMutationVariables,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TemplateAuthor } from './TemplateAuthor'

export const CustomTemplateSettingsBasicInfo: FC<{
  template: PerformanceProcessTemplateBasicInfoFragment
}> = ({ template }) => {
  const [updateBasicInfo, { loading }] = useMutation(
    UpdatePerformanceProcessTemplateBasicInfoDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.updateBasicInfoPerformanceReviewProcessCustomTemplate,
    }
  )

  const form = useForm<UpdatePerformanceProcessTemplateBasicInfoMutationVariables>({
    defaultValues: {
      id: template.id,
      name: template.name,
      description: template.description,
      templateDescription: template.templateDescription,
    },
    validate: (data) => {
      const errors: FormErrors<UpdatePerformanceProcessTemplateBasicInfoMutationVariables> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const response = await updateBasicInfo({
        variables: {
          id: template.id,
          name: data.name,
          description: data.description,
          templateDescription: data.templateDescription,
        },
      })

      const result = handleResult(response, () => {})

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return result
    },
  })

  const formHasName = !!form.values.name

  return (
    <Box gap="s32">
      <Text size="400" color="grey1000" weight="semibold">
        {i18n.t('performance_process.reviews.basic_information')}
      </Text>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <TextField
          mandatory
          readonly={loading}
          value={form.values.name || undefined}
          label={i18n.t('performance_process.reviews.basic_information_section.title_label')}
          placeholder={i18n.t(
            'performance_process.reviews.basic_information_section.title_placeholder'
          )}
          onChange={(name) => form.setValues({ ...form.values, name: name })}
        />
        <TextArea
          readonly={loading}
          value={form.values.description || ''}
          label={i18n.t('performance_process.reviews.basic_information_section.description_label')}
          placeholder={i18n.t(
            'performance_process.reviews.basic_information_section.description_placeholder_new_review'
          )}
          onChange={(description) => form.setValues({ ...form.values, description: description })}
        />
        <TextArea
          readonly={loading}
          value={form.values.templateDescription || ''}
          label={i18n.t('performance_process.reviews.templates.template_description')}
          placeholder={i18n.t(
            'performance_process.reviews.templates.save_as_template.template_description_placeholder'
          )}
          onChange={(templateDescription) =>
            form.setValues({ ...form.values, templateDescription: templateDescription })
          }
        />

        <Box justifyContent="flexEnd" flexDirection="row" width="full">
          <Button
            type={form.dirty ? 'primary' : 'secondary'}
            onClick={() => form.handleSubmit()}
            disabled={form.isSubmitting || !formHasName || loading || !form.dirty}
            loading={loading}
          >
            {i18n.t('common.save')}
          </Button>
        </Box>
      </Form>

      <TemplateAuthor template={template} />
    </Box>
  )
}
